import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, X, Sparkles, Upload, LineChart, Grid3x3, Gauge, FileText, Users2, Plug } from 'lucide-react';
import { activationApi, MilestoneKey } from '@/lib/activation';

const STEPS: Record<MilestoneKey, { icon: typeof Upload; defaultValue: string; path: string }> = {
  connect_source: { icon: Plug, defaultValue: 'Connect a data source', path: 'upload' },
  ingest_facts: { icon: Upload, defaultValue: 'Import your financial data', path: 'upload' },
  first_forecast: { icon: LineChart, defaultValue: 'Build a forecast', path: 'forecast' },
  first_pivot: { icon: Grid3x3, defaultValue: 'Save a pivot view', path: 'pivot' },
  first_kpi: { icon: Gauge, defaultValue: 'Track a KPI', path: 'kpis' },
  board_pack: { icon: FileText, defaultValue: 'Generate a board pack', path: 'board-pack' },
  invite_team: { icon: Users2, defaultValue: 'Invite a teammate', path: 'settings' },
};

const dismissKey = (wsId: string) => `cadencez.activation.dismissed.${wsId}`;

/**
 * Getting-started / activation checklist (Part 13). Milestones are derived
 * server-side from real workspace state; each row links to where to complete it.
 * Dismissible (per-workspace localStorage), auto-hides once every step is done.
 * The trial-days nudge lives inline so the growth loop is one card, not two banners.
 */
export function ActivationChecklist({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && window.localStorage.getItem(dismissKey(wsId)) === '1',
  );
  const { data } = useQuery({ queryKey: ['activation', wsId], queryFn: () => activationApi.summary(wsId), enabled: !!wsId, staleTime: 60_000, retry: false });

  if (dismissed || !data || data.doneCount === data.total) return null;

  const dismiss = () => { try { window.localStorage.setItem(dismissKey(wsId), '1'); } catch { /* ignore */ } setDismissed(true); };
  const pct = Math.round((data.doneCount / data.total) * 100);

  return (
    <div className="rounded-xl border border-accent-200 bg-accent-50/60 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent-600" />
            <h2 className="text-sm font-semibold text-ink-900">{t('activation.title', { defaultValue: 'Get started with CadencEZ' })}</h2>
          </div>
          <p className="mt-0.5 text-[12.5px] text-ink-600">{t('activation.subtitle', { defaultValue: `${data.doneCount} of ${data.total} steps done`, done: data.doneCount, total: data.total })}</p>
        </div>
        <button onClick={dismiss} aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })} className="text-ink-400 hover:text-ink-700 shrink-0"><X className="h-4 w-4" /></button>
      </div>

      <div className="mt-3 h-1.5 w-full rounded-full bg-accent-100 overflow-hidden">
        <div className="h-full bg-accent-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {data.milestones.map((m) => {
          const step = STEPS[m.key];
          const Icon = step.icon;
          return (
            <li key={m.key}>
              <Link to={`/app/workspace/${wsId}/${step.path}`} className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${m.done ? 'text-ink-500' : 'text-ink-800 hover:bg-white'}`}>
                {m.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <Circle className="h-4 w-4 text-ink-300 shrink-0" />}
                <Icon className="h-3.5 w-3.5 text-ink-400 shrink-0" />
                <span className={m.done ? 'line-through' : 'font-medium'}>{t(`activation.step.${m.key}`, { defaultValue: step.defaultValue })}</span>
              </Link>
            </li>
          );
        })}
      </ul>

      {data.trial.onTrial && (
        <div className="mt-3 pt-3 border-t border-accent-200/70 flex items-center justify-between gap-3 text-[12.5px]">
          <span className="text-ink-700">{t('activation.trial_left', { defaultValue: `Trial: ${data.trial.daysLeft} days left — keep Growth features`, days: data.trial.daysLeft })}</span>
          <Link to={`/app/workspace/${wsId}/subscription`} className="font-semibold text-accent-700 hover:text-accent-800 shrink-0">{t('activation.upgrade', { defaultValue: 'Upgrade →' })}</Link>
        </div>
      )}
    </div>
  );
}
